/**
 * IconComponent - Dynamic Icon Renderer for Blog Content
 * 
 * This component serves as a critical piece of the blog's dynamic content rendering system,
 * enabling safe and efficient display of icons throughout the application. It's specifically
 * designed to work with content management systems and dynamic data sources where icon
 * names are stored as strings and need to be resolved to actual React components at runtime.
 * 
 * Key Use Cases:
 * - Blog post metadata and category icons (LatestPosts.tsx)
 * - Topic grid displays with associated icons (HomeTopicsGrid.tsx)
 * - Email capture forms with contextual iconography (BlogEmailCapture.tsx)
 * - Trending topics with visual indicators (TrendingTopics.tsx)
 * - Homepage hero sections with interactive elements (BlogHomepageSimple.tsx)
 * 
 * IMPORTANT: This implementation uses a controlled icon mapping approach to prevent
 * bundling the entire @remixicon/react library, which would negatively impact
 * tree-shaking and bundle size. Only explicitly mapped icons are included in the
 * final bundle.
 * 
 * Design Considerations:
 * - Graceful degradation: Returns null for unmapped icons rather than throwing errors
 * - Consistent sizing: Applies default dimensions while allowing customization
 * - Type safety: Ensures proper TypeScript support for icon resolution
 * - Performance: Minimizes bundle size through selective icon imports
 * - Dual naming support: Handles both kebab-case and PascalCase icon names
 * 
 * @see HomeTopicsGrid.tsx - Uses this for topic category icons
 * @see TrendingTopics.tsx - Displays trending topic indicators
 * @see LatestPosts.tsx - Shows post category and metadata icons
 * @see BlogEmailCapture.tsx - Renders form interaction icons
 * @see BlogHomepageSimple.tsx - Hero section interactive elements
 */

import { type ComponentType } from 'react'
import { cn } from '~/lib/utils'

// Import only the icons we actually use to enable proper tree-shaking
// All imports consolidated into a single import statement for better tree-shaking
import {
  // Content and documentation icons
  RiArticleLine,
  RiBookOpenLine,
  RiNewspaperLine,
  // Technology and development icons
  RiCodeLine,
  RiComputerLine,
  RiDatabaseLine,
  RiSmartphoneLine,
  RiToolsLine,
  RiTestTubeLine,
  RiRobot2Line,
  // Business and analytics icons
  RiGlobalLine,
  RiLightbulbLine,
  RiRocketLine,
  RiBarChartBoxLine,
  RiLineChartLine,
  RiPieChartLine,
  RiDashboardLine,
  RiMoneyDollarCircleLine,
  // System and configuration icons
  RiSettings3Line,
  // UI and interaction icons
  RiArrowRightLine,
  RiArrowDownSLine,
  RiTimeLine,
  RiFireLine,
  RiLoader4Line,
  RiCheckLine,
  RiMailLine,
} from '~/icons/remix'

/**
 * Icon mapping registry - Controls which icons are available for dynamic rendering
 * 
 * This approach ensures:
 * 1. Only used icons are bundled (tree-shaking friendly)
 * 2. Type safety for icon name resolution
 * 3. Centralized control over available icons
 * 4. Easy maintenance and updates
 * 5. Support for both kebab-case and PascalCase naming conventions
 * 
 * When adding new icons:
 * 1. Import the icon component above
 * 2. Add both kebab-case and PascalCase mappings below
 * 3. Update the IconName type if using strict typing
 * 
 * Icons mapped based on blog config requirements:
 * - 'bar-chart-box': Creative Analytics topic
 * - 'line-chart': Ad Performance topic
 * - 'test-tube': Creative Testing & Data Science topics
 * - 'lightbulb': Creative Strategy topic
 * - 'newspaper': Industry Insights topic
 * - 'robot-2': AI in Advertising topic
 * - 'dashboard': Campaign Management topic
 * - 'global': Social Media Advertising topic
 * - 'code': Ad Tech topic
 * - 'money-dollar-circle': Media Buying topic
 * - 'pie-chart': Data Visualization topic
 * - 'article': General topic
 */
const ICON_MAP: Record<string, ComponentType<{ className?: string }>> = {
  // Content and documentation icons (dual naming support)
  'article': RiArticleLine,
  'RiArticleLine': RiArticleLine,
  'book-open': RiBookOpenLine,
  'RiBookOpenLine': RiBookOpenLine,
  'newspaper': RiNewspaperLine,
  'RiNewspaperLine': RiNewspaperLine,
  
  // Technology and development icons
  'code': RiCodeLine,
  'RiCodeLine': RiCodeLine,
  'computer': RiComputerLine,
  'RiComputerLine': RiComputerLine,
  'database': RiDatabaseLine,
  'RiDatabaseLine': RiDatabaseLine,
  'smartphone': RiSmartphoneLine,
  'RiSmartphoneLine': RiSmartphoneLine,
  'tools': RiToolsLine,
  'RiToolsLine': RiToolsLine,
  'test-tube': RiTestTubeLine,
  'RiTestTubeLine': RiTestTubeLine,
  'robot-2': RiRobot2Line,
  'RiRobot2Line': RiRobot2Line,
  
  // Business and analytics icons
  'global': RiGlobalLine,
  'RiGlobalLine': RiGlobalLine,
  'lightbulb': RiLightbulbLine,
  'RiLightbulbLine': RiLightbulbLine,
  'rocket': RiRocketLine,
  'RiRocketLine': RiRocketLine,
  'bar-chart-box': RiBarChartBoxLine,
  'RiBarChartBoxLine': RiBarChartBoxLine,
  'line-chart': RiLineChartLine,
  'RiLineChartLine': RiLineChartLine,
  'pie-chart': RiPieChartLine,
  'RiPieChartLine': RiPieChartLine,
  'dashboard': RiDashboardLine,
  'RiDashboardLine': RiDashboardLine,
  'money-dollar-circle': RiMoneyDollarCircleLine,
  'RiMoneyDollarCircleLine': RiMoneyDollarCircleLine,
  
  // System and configuration icons
  'settings-3': RiSettings3Line,
  'RiSettings3Line': RiSettings3Line,
  
  // UI and interaction icons
  'arrow-right': RiArrowRightLine,
  'RiArrowRightLine': RiArrowRightLine,
  'arrow-down-s': RiArrowDownSLine,
  'RiArrowDownSLine': RiArrowDownSLine,
  'time': RiTimeLine,
  'RiTimeLine': RiTimeLine,
  'fire': RiFireLine,
  'RiFireLine': RiFireLine,
  'loader-4': RiLoader4Line,
  'RiLoader4Line': RiLoader4Line,
  'check': RiCheckLine,
  'RiCheckLine': RiCheckLine,
  'mail': RiMailLine,
  'RiMailLine': RiMailLine,
} as const

/**
 * Type-safe icon name union for better developer experience
 * Automatically derived from the ICON_MAP keys
 */
export type IconName = keyof typeof ICON_MAP

/**
 * Props interface for the IconComponent
 */
interface IconComponentProps {
  /** 
   * The name of the icon to render - must match a key in ICON_MAP
   * Supports both kebab-case ('arrow-right') and PascalCase ('RiArrowRightLine') naming
   * Using string instead of IconName to maintain flexibility with CMS data
   */
  name: string
  /** 
   * Additional CSS classes to apply to the icon
   * Default classes (h-4 w-4) will be merged with these
   */
  className?: string
  /**
   * Accessible label for the icon - important for screen readers
   * If not provided, the icon will be treated as decorative (aria-hidden)
   */
  'aria-label'?: string
}

/**
 * IconComponent - Renders icons dynamically based on string names
 * 
 * This component is essential for rendering icons from dynamic content sources
 * like CMS data, API responses, or configuration files. It provides a safe
 * way to resolve string-based icon names to actual React components while
 * maintaining bundle size efficiency.
 * 
 * The component supports both naming conventions:
 * - kebab-case: 'arrow-right', 'line-chart', 'test-tube'
 * - PascalCase: 'RiArrowRightLine', 'RiLineChartLine', 'RiTestTubeLine'
 * 
 * This dual support ensures compatibility with different data sources and
 * existing code patterns throughout the application.
 * 
 * @param name - The icon name to resolve and render
 * @param className - Additional CSS classes for styling
 * @param aria-label - Accessibility label for screen readers
 * @returns The rendered icon component or null if icon not found
 * 
 * @example
 * ```tsx
 * // Basic usage with kebab-case
 * <IconComponent name="code" />
 * 
 * // Basic usage with PascalCase
 * <IconComponent name="RiCodeLine" />
 * 
 * // With custom styling
 * <IconComponent 
 *   name="bar-chart-box" 
 *   className="h-6 w-6 text-blue-500" 
 * />
 * 
 * // With accessibility
 * <IconComponent 
 *   name="article" 
 *   aria-label="Blog post"
 *   className="mr-2"
 * />
 * 
 * // Email capture form usage
 * <IconComponent 
 *   name="mail" 
 *   className="h-5 w-5"
 *   aria-label="Email subscription"
 * />
 * 
 * // Trending topics usage
 * <IconComponent 
 *   name="fire" 
 *   className="h-4 w-4 text-orange-500"
 *   aria-label="Trending"
 * />
 * ```
 */
export function IconComponent({ 
  name, 
  className,
  'aria-label': ariaLabel 
}: IconComponentProps): JSX.Element | null {
  // Resolve the icon component from our controlled mapping
  const IconComponent = ICON_MAP[name]
  
  // Gracefully handle unmapped icons - this prevents runtime errors
  // when CMS data contains icon names that haven't been added to our mapping
  if (!IconComponent) {
    // In development, we might want to log this for debugging
    if (process.env.NODE_ENV === 'development') {
      console.warn(`IconComponent: Unknown icon name "${name}". Add it to ICON_MAP if needed.`)
    }
    return null
  }
  
  return (
    <IconComponent 
      className={cn('h-4 w-4', className)}
      aria-label={ariaLabel}
      aria-hidden={!ariaLabel}
    />
  )
}

/**
 * Utility function to check if an icon name is available
 * Useful for conditional rendering or validation
 * Works with both kebab-case and PascalCase naming
 * 
 * @param name - The icon name to check
 * @returns True if the icon is available in the mapping
 */
export function isIconAvailable(name: string): name is IconName {
  return name in ICON_MAP
}

/**
 * Get all available icon names
 * Useful for generating lists or validation schemas
 * Returns both kebab-case and PascalCase variants
 * 
 * @returns Array of all available icon names
 */
export function getAvailableIconNames(): IconName[] {
  return Object.keys(ICON_MAP)
}

/**
 * Legacy compatibility function for existing icon-map.tsx usage
 * Provides backward compatibility while maintaining the new architecture
 * 
 * @param iconName - The icon name to resolve (kebab-case format)
 * @returns The icon component or RiArticleLine as fallback
 */
export function getIconComponent(iconName: string): ComponentType<{ className?: string }> {
  return ICON_MAP[iconName] ?? ICON_MAP.article ?? RiArticleLine
}